import { useCallback, useEffect, useState } from "react";
import { api, AttendanceCorrection, MyAttendanceHistory } from "../api";
import { Loading, ModalFrame } from "../components/ui";

function localDateTime(value?: string | null) {
  if (!value) return "";
  const date = new Date(value);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}T${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}:${String(date.getSeconds()).padStart(2, "0")}`;
}

const stamp = (value?: string | null) => value ? new Intl.DateTimeFormat(undefined, { dateStyle: "short", timeStyle: "medium" }).format(new Date(value)) : "—";
const statuses: Record<string, string> = { PENDING: "승인 대기", APPROVED: "승인", REJECTED: "반려" };

export function AttendanceCorrectionModal({ day, onClose, onCreated }: {
  day: MyAttendanceHistory["days"][number]; onClose: () => void; onCreated: () => void;
}) {
  const [start, setStart] = useState(localDateTime(day.firstClockInAt));
  const [end, setEnd] = useState(localDateTime(day.lastClockOutAt));
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const invalidTime = !start || !end || new Date(end) <= new Date(start) || new Date(end) > new Date();
  return <ModalFrame title="근무 이력 수정 요청" eyebrow={day.workDate} onClose={() => { if (!busy) onClose(); }}>
    <form className="modal-form attendance-correction-form" noValidate onSubmit={async (event) => {
      event.preventDefault(); setSubmitted(true); setError("");
      if (invalidTime || !reason.trim()) return;
      setBusy(true);
      try {
        await api.requestAttendanceCorrection({ workDate: day.workDate, startedAt: new Date(start).toISOString(), endedAt: new Date(end).toISOString(), reason: reason.trim() });
        onCreated();
      } catch (value) { setError(value instanceof Error ? value.message : "수정을 요청하지 못했습니다."); }
      finally { setBusy(false); }
    }}>
      <p className="span-2">근태 관리자가 승인하면 반영됩니다. 하루에 여러 번 출퇴근했다면 첫 출근과 마지막 퇴근만 변경하고 중간 근무 구간은 유지합니다. 시각은 현재 기기의 시간대로 표시됩니다.</p>
      <label>출근 시각 <input aria-label="수정 출근 시각" type="datetime-local" step="1" value={start} aria-invalid={submitted && invalidTime} onChange={(event) => setStart(event.target.value)} /></label>
      <label>퇴근 시각 <input aria-label="수정 퇴근 시각" type="datetime-local" step="1" value={end} aria-invalid={submitted && invalidTime} onChange={(event) => setEnd(event.target.value)} /></label>
      {submitted && invalidTime && <p className="error-box span-2">출퇴근 시각을 확인해 주세요. 퇴근은 출근 이후이며 미래 시각일 수 없습니다.</p>}
      <label className="span-2"><span>수정 사유 <span className="required">*</span></span><textarea aria-label="수정 사유" maxLength={2000} value={reason} aria-invalid={submitted && !reason.trim()} onChange={(event) => setReason(event.target.value)} /></label>
      {submitted && !reason.trim() && <p className="error-box span-2">수정 사유를 입력해 주세요.</p>}
      {error && <p className="error-box span-2">{error}</p>}
      <footer className="span-2"><button type="button" disabled={busy} onClick={onClose}>취소</button><button type="submit" className="primary" disabled={busy}>{busy ? "요청 중…" : "수정 요청 보내기"}</button></footer>
    </form>
  </ModalFrame>;
}

export function AttendanceCorrectionHistory({ management = false, revision = 0, requestId, onChanged }: {
  management?: boolean; revision?: number; requestId?: string; onChanged?: () => void;
}) {
  const [items, setItems] = useState<AttendanceCorrection[] | null>(null);
  const [selected, setSelected] = useState<AttendanceCorrection | null>(null);
  const [decision, setDecision] = useState<"APPROVE" | "REJECT">("APPROVE");
  const [comment, setComment] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try { setItems(await api.attendanceCorrections(management)); }
    catch (value) { setError(value instanceof Error ? value.message : "수정 요청을 불러오지 못했습니다."); }
  }, [management]);
  useEffect(() => { void load(); }, [load, revision]);
  useEffect(() => {
    if (requestId) setSelected(items?.find((item) => item.id === requestId && item.status === "PENDING") || null);
  }, [items, requestId]);
  return <section className="section-card people-table-card">
    <header><h2>{management ? "근무 수정 요청" : "내 근무 수정 요청"}</h2></header>
    {error && !selected && <div className="error-box">{error}</div>}
    {!items ? !error && <Loading layout="table" label="수정 요청을 불러오는 중" /> : <div className="attendance-table-scroll"><table className="attendance-table">
      <thead><tr>{management && <th>구성원</th>}<th>근무일</th><th>요청 출퇴근</th><th>사유</th><th>상태</th><th>검토</th></tr></thead>
      <tbody>{items.map((item) => <tr key={item.id}>
        {management && <td>{item.displayName}</td>}<td>{item.workDate}</td>
        <td>{stamp(item.startedAt)}<br />{stamp(item.endedAt)}</td>
        <td className="attendance-correction-reason">{item.reason}</td><td>{statuses[item.status] || item.status}</td>
        <td>{management && item.status === "PENDING" ? <button type="button" onClick={() => { setSelected(item); setComment(""); setDecision("APPROVE"); setError(""); }}>검토</button> : <span>{item.reviewComment || "—"}{item.reviewedAt && <small className="block">{stamp(item.reviewedAt)}</small>}</span>}</td>
      </tr>)}{!items.length && <tr><td colSpan={management ? 6 : 5} className="attendance-empty">수정 요청이 없습니다.</td></tr>}</tbody>
    </table></div>}
    {selected && <ModalFrame title="근무 수정 요청 검토" eyebrow={`${selected.displayName} · ${selected.workDate}`} onClose={() => { if (!busy) setSelected(null); }}>
      <form className="modal-form attendance-correction-form" onSubmit={async (event) => {
        event.preventDefault(); setError("");
        if (decision === "REJECT" && !comment.trim()) { setError("반려 사유를 입력해 주세요."); return; }
        setBusy(true);
        try { await api.decideAttendanceCorrection(selected.id, decision, comment.trim()); setSelected(null); await load(); onChanged?.(); }
        catch (value) { setError(value instanceof Error ? value.message : "수정 요청을 처리하지 못했습니다."); }
        finally { setBusy(false); }
      }}>
        <div className="span-2"><p>기존: {stamp(selected.originalStartedAt)} ~ {stamp(selected.originalEndedAt)}</p><p>변경: {stamp(selected.startedAt)} ~ {stamp(selected.endedAt)}</p><p className="attendance-correction-reason">사유: {selected.reason}</p></div>
        <label className="span-2">검토 결과<select aria-label="검토 결과" value={decision} onChange={(event) => setDecision(event.target.value as typeof decision)}><option value="APPROVE">승인</option><option value="REJECT">반려</option></select></label>
        <label className="span-2"><span>검토 사유 {decision === "REJECT" && <span className="required">*</span>}</span><textarea aria-label="검토 사유" maxLength={2000} value={comment} onChange={(event) => setComment(event.target.value)} /></label>
        {error && <p className="error-box span-2">{error}</p>}
        <footer className="span-2"><button type="button" disabled={busy} onClick={() => setSelected(null)}>닫기</button><button type="submit" disabled={busy} className={decision === "REJECT" ? "danger" : "primary"}>{busy ? "처리 중…" : decision === "REJECT" ? "반려" : "승인하고 저장"}</button></footer>
      </form>
    </ModalFrame>}
  </section>;
}
